// components/team/InvitationsPage.tsx
import React, { useState } from 'react';
import { FiXCircle, FiRefreshCcw } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';
import { getAuthContext } from '../../utils/jwt';
import { APIMember } from '../../features/members/membersSlice';
import { BASE_URL } from '../../utils/constants';

interface InvitationsPageProps {
    invitations: APIMember[];
    displayEmail?: string;
    onCancelInvite?: (index: number) => void;
}

const FiXCircleComponent = FiXCircle as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

const FiRefreshCcwComponent = FiRefreshCcw as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

const statusClasses: Record<string, string> = {
    Pending: 'bg-yellow-100 text-yellow-600 px-3 py-1 rounded-full text-sm',
    Expired: 'bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm',
    Canceled: 'bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm',
};

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const getDisplayStatus = (status: string): 'Pending' | 'Expired' | 'Canceled' => {
    const statusMap: Record<string, 'Pending' | 'Expired' | 'Canceled'> = {
        'PENDING': 'Pending',
        'EXPIRED': 'Expired',
        'CANCELED': 'Canceled',
    };
    return statusMap[status] || 'Pending';
};

const InvitationsPage: React.FC<InvitationsPageProps> = ({
    invitations,
    displayEmail,
    onCancelInvite
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // ✅ Show ALL invitations including 'Deleted role'
    const formattedInvitations = invitations.map((invite, index) => {
        const status = getDisplayStatus(invite.status);
        return {
            email: invite.email,
            role: invite.roleName || 'No Role',
            invitedBy: invite.displayName || 'Unknown',
            invitedOn: formatDate(invite.addedAt),
            status: status,
            rawIndex: index,
            inviteId: invite.inviteId,
            memberId: invite.memberId,
            tenantId: invite.tenantId,
        };
    });

    const totalPages = Math.ceil(formattedInvitations.length / itemsPerPage);
    const paginatedInvitations = formattedInvitations.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Calculate start and end items
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, formattedInvitations.length);

    // Handle page change with smooth scroll
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleResendInvite = async (invite: typeof formattedInvitations[0]) => {
        try {
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                toast.error('Authentication required');
                return;
            }

            if (!invite.inviteId) {
                toast.error('No invite found');
                return;
            }

            const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
            const loadingToast = toast.loading('Resending invitation...');

            const response = await fetch(`${BASE_URL}/v1/team/invites/${invite.inviteId}/resend`, {
                method: 'POST',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                    'Idempotency-Key': idempotencyKey,
                    'Content-Type': 'application/json',
                },
            });

            toast.dismiss(loadingToast);

            if (!response.ok) {
                throw new Error(`Failed to resend invite: ${response.status}`);
            }

            toast.success('Invitation resent successfully!');

        } catch (error: any) {
            toast.error(error.message || 'Failed to resend invite');
            // console.error('Resend invite error:', error);
        }
    };

    const handleCancelInvite = async (invite: typeof formattedInvitations[0]) => {
        try {
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                toast.error('Authentication required');
                return;
            }

            if (!invite.inviteId) {
                toast.error('No invite found');
                return;
            }

            const loadingToast = toast.loading('Cancelling invitation...');

            const response = await fetch(`${BASE_URL}/v1/team/invites/${invite.inviteId}`, {
                method: 'DELETE',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                },
            });

            toast.dismiss(loadingToast);

            if (!response.ok) {
                throw new Error(`Failed to cancel invite: ${response.status}`);
            }

            toast.success('Invitation cancelled successfully!');

            // Update Redux state immediately
            if (onCancelInvite) {
                onCancelInvite(invite.rawIndex);
            }

        } catch (error: any) {
            toast.error(error.message || 'Failed to cancel invite');
            // console.error('Cancel invite error:', error);
        }
    };

    if (invitations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="bg-gray-50 rounded-full p-4 mb-4">
                    <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                    </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No invitations</h3>
                <p className="text-gray-500 text-center max-w-sm">
                    There are no pending or past invitations to display.
                </p>
            </div>
        );
    }

    return (
        <div>
            <table className='w-full text-left mt-4 border border-gray-200'>
                <thead className='bg-gray-100'>
                    <tr
                        className='text-gray-600 text-sm border-b'
                        style={{ fontSize: '12px', color: '#657584' }}
                    >
                        <th className='px-6 py-3 font-normal'>EMAIL</th>
                        <th className='px-6 py-3 font-normal'>ROLE</th>
                        <th className='px-6 py-3 font-normal'>INVITED BY</th>
                        <th className='px-6 py-3 font-normal'>INVITED ON</th>
                        <th className='px-6 py-3 font-normal'>STATUS</th>
                        <th className='px-6 py-3 font-normal'>ACTIONS</th>
                    </tr>
                </thead>
                <tbody style={{ color: '#3F5265', fontSize: '14px' }}>
                    {paginatedInvitations.map((invite) => {
                        // Check if this invitation email matches the current user's email
                        const isCurrentUser = displayEmail && invite.email === displayEmail;

                        return (
                            <tr
                                key={invite.rawIndex}
                                className='border-b hover:bg-gray-50'
                            >
                                <td className='px-6 py-3'>{invite.email}</td>
                                <td className='px-6 py-3'>{invite.role}</td>
                                <td className='px-6 py-3'>{invite.invitedBy}</td>
                                <td className='px-6 py-3'>{invite.invitedOn}</td>
                                <td className='px-6 py-3'>
                                    <span className={statusClasses[invite.status]}>
                                        {invite.status}
                                    </span>
                                </td>
                                <td className='px-6 py-3'>
                                    <div className='flex items-center space-x-3'>
                                        {(invite.status === 'Canceled' || invite.status === 'Expired') && (
                                            <>
                                                {isCurrentUser ? (
                                                    // Show "Already Accepted" for current user
                                                    <span className="text-green-600 text-sm font-medium flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Already Accepted
                                                    </span>
                                                ) : (
                                                    // Show "Re-send Invite" for other users
                                                    <button
                                                        onClick={() => handleResendInvite(invite)}
                                                        className="text-[#00A4BD] hover:text-[#00899E] text-sm font-medium transition-colors flex items-center gap-1"
                                                    >
                                                        <FiRefreshCcwComponent className='w-4 h-4' />
                                                        Re-send Invite
                                                    </button>
                                                )}
                                            </>
                                        )}

                                        {invite.status === 'Pending' && !isCurrentUser && (
                                            <button
                                                onClick={() => handleCancelInvite(invite)}
                                                className="text-red-500 hover:text-red-700 text-sm font-medium transition-colors flex items-center gap-1"
                                            >
                                                <FiXCircleComponent className='w-5 h-5' />
                                                Cancel Invite
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Modern Pagination */}
            <div className='flex items-center justify-between mt-4 text-sm text-gray-500 border-t border-gray-200 pt-4'>
                <span>
                    Showing {startItem} to {endItem} of {formattedInvitations.length} invitations
                </span>

                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>

                        {(() => {
                            const pages = [];
                            const total = totalPages;
                            const current = currentPage;
                            const delta = 1;

                            pages.push(1);

                            let rangeStart = Math.max(2, current - delta);
                            let rangeEnd = Math.min(total - 1, current + delta);

                            if (rangeStart > 2) {
                                pages.push('...');
                            }

                            for (let i = rangeStart; i <= rangeEnd; i++) {
                                pages.push(i);
                            }

                            if (rangeEnd < total - 1) {
                                pages.push('...');
                            }

                            if (total > 1) {
                                pages.push(total);
                            }

                            return pages.map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <span
                                            key={`ellipsis-${index}`}
                                            className="px-3 py-2 text-sm text-gray-500"
                                        >
                                            …
                                        </span>
                                    );
                                }

                                const pageNumber = page as number;
                                const isActive = pageNumber === current;

                                return (
                                    <button
                                        key={pageNumber}
                                        onClick={() => handlePageChange(pageNumber)}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                                            ? 'bg-[#00A4BD] text-white'
                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            });
                        })()}

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
            <ToastContainer position='top-right' autoClose={3000} />
        </div>
    );
};

export default InvitationsPage;